/**
 * Catalogue des personas de l'assistant, pour le site (page /assistant).
 *
 * Copie du catalogue codé du back (`libs/assistant/src/personas/persona-catalog.ts`,
 * la même source que `GET /assistant/personas`, réservé aux clients connectés).
 * Look (forme, expression, encres) et textes affichés ; jamais le prompt de
 * voix. À resynchroniser quand la console change un personnage.
 */
import type { ExpressionId } from "@/lib/bloub/expressions";
import type { ShapeId } from "@/lib/bloub/skins";

export interface PersonaCopy {
  tagline: string;
  character: string;
  voice: string;
  fits: string;
  starters: string[];
}

export interface Persona {
  id: string;
  name: string;
  shape: ShapeId;
  expression: ExpressionId;
  ink: { light: string; dark: string };
  copy: { fr: PersonaCopy; en: PersonaCopy };
}

export const PERSONAS: readonly Persona[] = [
  {
    id: "lina",
    name: "Lina",
    shape: "galet",
    expression: "ravi",
    ink: { light: "#03331a", dark: "#08f779" },
    copy: {
      fr: {
        tagline: "Coucou, moi c'est Lina. Dis-moi ce qui te ferait plaisir.",
        character: "Accueillante et généreuse, un peu maternelle. Elle aime nourrir les gens, rassure sur les délais et célèbre une commande passée.",
        voice: "« Ça, ça va vous faire du bien. »",
        fits: "Pour tout le monde.",
        starters: ["Qu’est-ce que je mange ce soir ?", "Ma commande habituelle"],
      },
      en: {
        tagline: "Hi, I'm Lina. Tell me what you feel like eating.",
        character: "Warm and generous, a little motherly. She likes feeding people, reassures about timing and celebrates a placed order.",
        voice: "\"That one will do you good.\"",
        fits: "For everyone.",
        starters: ["What should I eat tonight?", "My usual order"],
      },
    },
  },
  {
    id: "sami",
    name: "Sami",
    shape: "squircle",
    expression: "curieux",
    ink: { light: "#f08a24", dark: "#f0b429" },
    copy: {
      fr: {
        tagline: "Salut, moi c'est Sami. On essaie quelque chose de nouveau ?",
        character: "Explorateur, gourmand de nouveautés. Il connaît les petites adresses, les plats du jour, ce qui vient d'ouvrir.",
        voice: "« Il y a un nouveau libanais à Hydra, ouvert tard. Vous voulez voir ? »",
        fits: "Pour ceux qui aiment découvrir, et les soirs sans idée.",
        starters: ["Surprends-moi", "Qu’est-ce qui vient d’ouvrir ?"],
      },
      en: {
        tagline: "Hey, I'm Sami. Shall we try something new?",
        character: "An explorer with an appetite for novelty. He knows the small places, the daily specials, what just opened.",
        voice: "\"There is a new Lebanese place in Hydra, open late. Want to see?\"",
        fits: "For the curious, and for evenings with no idea.",
        starters: ["Surprise me", "What's new around me?"],
      },
    },
  },
  {
    id: "yasmine",
    name: "Yasmine",
    shape: "goutte",
    expression: "attentif",
    ink: { light: "#116b5b", dark: "#2fbfa0" },
    copy: {
      fr: {
        tagline: "Bonjour, je suis Yasmine. Dites-moi vos contraintes, je m'occupe du reste.",
        character: "Calme, méthodique, soucieuse des détails. Elle vérifie les allergènes, le budget, l'heure de livraison, et ne promet rien qu'elle n'ait lu dans une carte.",
        voice: "« Ce plat contient des arachides. Je vous propose deux alternatives sans. »",
        fits: "Pour les commandes contraintes : régime, allergies, budget, groupe.",
        starters: ["Sans arachides ce soir", "J’ai un budget serré"],
      },
      en: {
        tagline: "Hello, I'm Yasmine. Tell me your constraints, I'll handle the rest.",
        character: "Calm, methodical, detail-minded. She checks allergens, budget and delivery time, and promises nothing a card has not shown.",
        voice: "\"This dish contains peanuts. Here are two alternatives without.\"",
        fits: "For constrained orders: diets, allergies, budget, groups.",
        starters: ["No peanuts tonight", "I am on a budget"],
      },
    },
  },
  {
    id: "rayan",
    name: "Rayan",
    shape: "hexagone",
    expression: "excite",
    ink: { light: "#6d28d9", dark: "#a78bfa" },
    copy: {
      fr: {
        tagline: "Yo, moi c'est Rayan. On trouve le bon plan du soir ?",
        character: "Rapide, joueur, chasseur de bons plans. Il aime les promotions, les combos, et il va droit au but.",
        voice: "« Code promo appliqué. On valide ? »",
        fits: "Pour les pressés, les soirs de rush, les sensibles au prix.",
        starters: ["Les promos du jour", "Le moins cher autour de moi"],
      },
      en: {
        tagline: "Yo, I'm Rayan. Shall we find tonight's deal?",
        character: "Quick, playful, a deal hunter. He likes promos and combos, and gets to the point.",
        voice: "\"Promo code applied. Shall we confirm?\"",
        fits: "For people in a hurry, rush hours, price-sensitive orders.",
        starters: ["Today's deals", "Cheapest near me"],
      },
    },
  },
  {
    id: "mila",
    name: "Mila",
    shape: "nuage",
    expression: "espiegle",
    ink: { light: "#a3196f", dark: "#f472c0" },
    copy: {
      fr: {
        tagline: "Hey, moi c'est Mila. On se fait plaisir ce soir ?",
        character: "Légère, drôle, complice. Elle dédramatise une boutique fermée et glisse un mot sur le dessert, sans en faire trop.",
        voice: "« Fermé ce soir. Mais leur voisin fait le même tacos, et il est ouvert. »",
        fits: "Pour les commandes détendues : soirée, week-end.",
        starters: ["Un truc réconfortant", "Et le dessert ?"],
      },
      en: {
        tagline: "Hey, I'm Mila. Shall we treat ourselves tonight?",
        character: "Light, funny, complicit. She defuses a closed shop and slips in a word about dessert, without overdoing it.",
        voice: "\"Closed tonight. But their neighbour makes the same tacos, and they are open.\"",
        fits: "For relaxed orders: evenings, weekends.",
        starters: ["Something comforting", "What about dessert?"],
      },
    },
  },
  {
    id: "anis",
    name: "Anis",
    shape: "capsule",
    expression: "serein",
    ink: { light: "#1d4ed8", dark: "#60a5fa" },
    copy: {
      fr: {
        tagline: "Bonsoir, Anis. Laissez-moi choisir, vous ne serez pas déçu.",
        character: "Sûr de lui, gourmet, un peu classique. Il recommande les valeurs sûres et sait défendre ses choix.",
        voice: "« La margherita de Cook’s. Pâte fine, cuite au feu de bois. »",
        fits: "Pour les indécis, et les commandes « comme d’habitude ».",
        starters: ["Choisis pour moi", "Comme d’habitude"],
      },
      en: {
        tagline: "Good evening, Anis here. Let me choose, you won't regret it.",
        character: "Confident, a gourmet, a little classic. He recommends the safe bets and can defend his choices.",
        voice: "\"The margherita at Cook's. Thin base, wood-fired.\"",
        fits: "For the undecided, and for \"the usual\".",
        starters: ["Choose for me", "The usual"],
      },
    },
  },
  {
    id: "nour",
    name: "Nour",
    shape: "galet",
    expression: "neutre",
    ink: { light: "#334155", dark: "#e2e8f0" },
    copy: {
      fr: {
        tagline: "Nour. Dites-moi ce qu’il vous faut.",
        character: "Sobre et limpide. Elle va à l’essentiel, sans ornement : une phrase, la bonne information.",
        voice: "« Trois boutiques ouvertes. La plus proche ferme dans une heure. »",
        fits: "Pour ceux qui veulent aller vite et sans bavardage.",
        starters: ["Boutiques ouvertes", "Où en est ma commande ?"],
      },
      en: {
        tagline: "Nour. Tell me what you need.",
        character: "Plain and clear. She gets to the point, no ornament: one sentence, the right information.",
        voice: "\"Three shops open. The closest closes within the hour.\"",
        fits: "For people who want speed and no chatter.",
        starters: ["Open shops", "Where is my order?"],
      },
    },
  },
];

export function personaInk(p: Persona, dark: boolean): string {
  return dark ? p.ink.dark : p.ink.light;
}
